// ============================================
// components/gunny/GunnyFilterBar.tsx
// Compact filter controls for MP/CAO dashboards. Options are derived from
// the currently-loaded rows so there's no extra API call.
// ============================================
import React, { useMemo } from 'react';
import { Search } from 'lucide-react';
import type { GunnyProformaRow } from '../../api/services';

export interface GunnyFilterState {
  district: string;   // '' = all; compare against row.district (name)
  supplier: string;   // '' = all; compare against row.merchant
  q: string;          // free text
}

export const emptyFilter: GunnyFilterState = { district: '', supplier: '', q: '' };

export function applyFilters(rows: GunnyProformaRow[], f: GunnyFilterState): GunnyProformaRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.district && r.district !== f.district) return false;
    if (f.supplier && r.merchant !== f.supplier) return false;
    if (q) {
      const hay = [
        r.society_name, r.merchant, r.district, r.supplier_invoice_no,
        r.eway_bill_no, r.tender_id,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

interface Props {
  rows: GunnyProformaRow[];
  value: GunnyFilterState;
  onChange: (v: GunnyFilterState) => void;
}

export const GunnyFilterBar: React.FC<Props> = ({ rows, value, onChange }) => {
  const districts = useMemo(() => Array.from(new Set(rows.map((r) => r.district))).sort(), [rows]);
  const suppliers = useMemo(() => Array.from(new Set(rows.map((r) => r.merchant))).sort(), [rows]);

  return (
    <div className="flex flex-wrap gap-2 mb-3 items-center">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
        <input
          type="search"
          placeholder="Search society, supplier, invoice…"
          value={value.q}
          onChange={(e) => onChange({ ...value, q: e.target.value })}
          className="pl-7 pr-3 py-1.5 text-sm border rounded w-60"
        />
      </div>
      <select
        value={value.district}
        onChange={(e) => onChange({ ...value, district: e.target.value })}
        className="px-2 py-1.5 text-sm border rounded bg-white"
      >
        <option value="">All districts</option>
        {districts.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <select
        value={value.supplier}
        onChange={(e) => onChange({ ...value, supplier: e.target.value })}
        className="px-2 py-1.5 text-sm border rounded bg-white max-w-[260px]"
      >
        <option value="">All suppliers</option>
        {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {(value.district || value.supplier || value.q) && (
        <button
          onClick={() => onChange(emptyFilter)}
          className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 underline"
        >
          Clear
        </button>
      )}
    </div>
  );
};

export default GunnyFilterBar;
